import { Box, HStack, Text } from "@chakra-ui/layout";
import { Checkbox, Portal } from "@chakra-ui/react";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  type DropAnimation,
  defaultDropAnimationSideEffects,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToHorizontalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { t } from "@lingui/macro";
import { TaskOrderByColumnEnum } from "@src/__generated__/graphql";
import type { TaskRenderColumnSimple } from "@src/components/modules/resource-planning/tasks/listing/builders";
import { TaskHeaderItem } from "@src/components/modules/resource-planning/tasks/listing/components/header-item";
import { SortTitle } from "@src/components/ui-kit/SortTitle";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

export const TaskListingTableHeader = observer(
  function TaskListingTableHeader() {
    const { authStore, tasksListingStore: store } = useStore();

    const mouseSensor = useSensor(MouseSensor);
    const pointerSensor = useSensor(PointerSensor);
    const touchSensor = useSensor(TouchSensor);
    const sensors = useSensors(mouseSensor, touchSensor, pointerSensor);

    if (!store.columnsStore.titleColumn) return null;

    return (
      <HStack
        spacing="0"
        pr="4"
        bg="white"
        minW="fit-content"
        borderBottom="1px"
        borderColor="gray.200"
        position="sticky"
        top="0"
        zIndex="2"
        left="0"
        fontWeight="bold"
        alignItems="center"
      >
        <DndContext
          key={store.filtersStore.orderBy.asURLSearchParam.orderBy}
          modifiers={[
            restrictToHorizontalAxis,
            restrictToFirstScrollableAncestor,
          ]}
          onDragEnd={store.columnsStore.handleReorderEnd}
          onDragStart={store.columnsStore.handleReorderStart}
          sensors={sensors}
          collisionDetection={closestCorners}
          autoScroll={{ threshold: { x: 0.2, y: 0 } }}
        >
          <SortableContext
            items={store.columnsStore.columnKeysToRender}
            strategy={horizontalListSortingStrategy}
          >
            <TaskHeaderItem
              sx={{
                position: "sticky",
                left: "0",
                backgroundColor: "white",
                zIndex: "1",
                overflow: "hidden",
                textOverflow: "clip",
              }}
              handleZIndex={2}
              py="3"
              pl="4"
              borderRight="1px"
              borderRightColor={
                store.horizontallyScrolled ? "gray.200" : "transparent"
              }
              h="40px"
              onResizeStart={store.columnsStore.handleColumnResizeStart}
              onColumnResize={store.columnsStore.handleColumnResize}
              onResizeStop={store.columnsStore.handleColumnResizeStop}
              column={store.columnsStore.titleColumn}
            >
              {authStore.isInternalUser && (
                <Checkbox
                  mr="2"
                  isChecked={store.bulkActionsStore.isSelectAllChecked}
                  isIndeterminate={
                    store.bulkActionsStore.isSelectAllIndeterminate
                  }
                  onChange={(e) => {
                    e.stopPropagation();
                    store.bulkActionsStore.handleToggleAll();
                  }}
                />
              )}
              <SortTitle
                title={t`Title`}
                order={store.filtersStore.orderBy.getOrderForColumn(
                  TaskOrderByColumnEnum.TaskName,
                )}
                column={TaskOrderByColumnEnum.TaskName}
                onOrderChange={(clause) =>
                  store.filtersStore.handleOrderChange(clause)
                }
              />
            </TaskHeaderItem>

            {store.columnsStore.columnsToRender.map((column, index, array) => (
              <TaskHeaderItem
                key={`column-header-${column.key}`}
                borderLeft="1px"
                isTruncated
                borderLeftColor={index === 0 ? "gray.200" : "transparent"}
                borderRight="1px"
                borderRightColor={
                  index < array.length - 1 ? "gray.200" : "transparent"
                }
                column={column}
                onResizeStart={store.columnsStore.handleColumnResizeStart}
                onColumnResize={store.columnsStore.handleColumnResize}
                onResizeStop={store.columnsStore.handleColumnResizeStop}
                isReorderable
              >
                {/* {column.key === "comments" ? (
                  <Box h="20px" />
                ) : (
                  <> */}
                {column.orderBy ? (
                  <SortTitle
                    title={column.title}
                    order={
                      column.orderBy
                        ? store.filtersStore.orderBy.getOrderForColumn(
                            column.orderBy,
                          )
                        : undefined
                    }
                    column={column.orderBy}
                    onOrderChange={store.filtersStore.handleOrderChange}
                  />
                ) : (
                  <Text fontWeight="inherit">{column.title}</Text>
                )}
                {/* </>
                )} */}
              </TaskHeaderItem>
            ))}

            <DraggedItem draggedColumn={store.columnsStore.draggedColumn} />
          </SortableContext>
        </DndContext>
      </HStack>
    );
  },
);

type DraggedItemProps = {
  draggedColumn: TaskRenderColumnSimple | undefined;
};
const DraggedItem = observer(function DraggedItem({
  draggedColumn,
}: DraggedItemProps) {
  return (
    <Portal>
      <DragOverlay
        dropAnimation={dropAnimation}
        modifiers={[restrictToWindowEdges]}
      >
        {draggedColumn && (
          <TaskHeaderItem
            cursor="grabbing"
            isReorderable
            bg="white"
            h="25px"
            column={draggedColumn}
            zIndex="2"
          >
            {draggedColumn.key === "comments" ? (
              <Box h="20px" />
            ) : (
              <Text fontWeight="bold">{draggedColumn.title}</Text>
            )}
          </TaskHeaderItem>
        )}
      </DragOverlay>
    </Portal>
  );
});
